import {
  TILE_COLORS,
  type Cell,
  type MatchState,
  type Position,
  type RefillTile,
  type Special,
  type Tile,
  type TileColor,
} from './types';
import { rngInt } from './rng';
import type { GravityMove } from './types';

export function posKey(p: Position): string {
  return `${p.r},${p.c}`;
}

export function byPos(a: Position, b: Position): number {
  return a.r - b.r || a.c - b.c;
}

export function isActive(state: MatchState, r: number, c: number): boolean {
  if (r < 0 || c < 0 || r >= state.config.boardH || c >= state.config.boardW) return false;
  if (!state.mask) return true;
  return state.mask[r][c];
}

export function inBounds(state: MatchState, p: Position): boolean {
  return p.r >= 0 && p.c >= 0 && p.r < state.config.boardH && p.c < state.config.boardW;
}

function cloneTile(tile: Tile): Tile {
  return {
    id: tile.id,
    color: tile.color,
    special: tile.special,
    statuses: tile.statuses?.map((status) => ({ ...status })),
    ...(tile.role ? { role: tile.role } : {}),
  };
}

export function cloneState(state: MatchState): MatchState {
  return {
    seed: state.seed,
    rngState: state.rngState,
    config: { ...state.config },
    mask: state.mask ? state.mask.map((row) => row.slice()) : null,
    board: state.board.map((row) => row.map((cell) => (cell ? cloneTile(cell) : null))),
    nextTileId: state.nextTileId,
    players: [clonePlayer(state.players[0]), clonePlayer(state.players[1])],
    currentPlayer: state.currentPlayer,
    phase: state.phase,
    winner: state.winner,
    bonusMovesGrantedThisTurn: state.bonusMovesGrantedThisTurn,
    ...(state.lastClear ? { lastClear: { ...state.lastClear } } : {}),
    ...(state.venue
      ? {
          venue: {
            ...state.venue,
            path: state.venue.path.slice(),
            actors: state.venue.actors.map((item) => ({ ...item })),
            treats: [state.venue.treats[0], state.venue.treats[1]] as [number, number],
          },
        }
      : {}),
  };
}

function clonePlayer(player: MatchState['players'][number]): MatchState['players'][number] {
  return {
    ...player,
    loadout: player.loadout.map((slot) => ({ ...slot })),
    hearth: player.hearth ? { ...player.hearth } : undefined,
  };
}

export function boardToString(state: MatchState): string {
  return state.board
    .map((row) =>
      row
        .map((cell) => {
          if (!cell) return '...';
          const tag = cell.special ? `:${cell.special}` : '';
          return `${cell.color}${tag}`;
        })
        .join(' '),
    )
    .join('\n');
}

/**
 * Paint colors onto a board. Tokens are `color`, `color:special`, or `.` for empty.
 * Existing tile ids are kept so tests can track pieces across a paint.
 * Painting replaces the tile, so statuses on that cell are cleared.
 */
export function paintBoard(state: MatchState, rows: readonly string[]): void {
  for (let r = 0; r < rows.length && r < state.config.boardH; r++) {
    const tokens = rows[r].trim().split(/\s+/);
    for (let c = 0; c < tokens.length && c < state.config.boardW; c++) {
      const token = tokens[c];
      if (token === '.' || token === '..' || token === '...') {
        state.board[r][c] = null;
        continue;
      }
      const [colorName, specialName] = token.split(':');
      const color = colorName as TileColor;
      const special = (specialName as Special | undefined) ?? null;
      const existing = state.board[r][c];
      const id = existing?.id ?? state.nextTileId++;
      state.board[r][c] = { id, color, special: special || null };
      if (id >= state.nextTileId) state.nextTileId = id + 1;
    }
  }
}

/** Fill active cells with colors that do not form a 3-run with already filled neighbors. */
export function fillAvoidingMatches(state: MatchState, keepIds = false): void {
  const h = state.config.boardH;
  const w = state.config.boardW;
  for (let r = 0; r < h; r++) {
    for (let c = 0; c < w; c++) {
      if (!isActive(state, r, c)) {
        state.board[r][c] = null;
        continue;
      }
      const forbidden = new Set<TileColor>();
      if (c >= 2) {
        const a = state.board[r][c - 1];
        const b = state.board[r][c - 2];
        if (a && b && a.special !== 'rainbow' && b.special !== 'rainbow' && a.color === b.color) {
          forbidden.add(a.color);
        }
      }
      if (r >= 2) {
        const a = state.board[r - 1][c];
        const b = state.board[r - 2][c];
        if (a && b && a.special !== 'rainbow' && b.special !== 'rainbow' && a.color === b.color) {
          forbidden.add(a.color);
        }
      }
      const pool = TILE_COLORS.filter((color) => !forbidden.has(color));
      const color = pool[rngInt(state, pool.length)];
      const existing = state.board[r][c];
      if (keepIds && existing) {
        existing.color = color;
        existing.special = null;
      } else {
        state.board[r][c] = { id: state.nextTileId++, color, special: null };
      }
    }
  }
}

/**
 * Pack each column downward. Inactive mask cells are holes: tiles fall through
 * them and land on the next playable cell. Returns only tiles that changed row.
 */
export function applyGravity(state: MatchState): GravityMove[] {
  const moves: GravityMove[] = [];
  const h = state.config.boardH;
  const w = state.config.boardW;
  for (let c = 0; c < w; c++) {
    const tiles: { tile: Tile; from: number }[] = [];
    const activeRows: number[] = [];
    for (let r = 0; r < h; r++) {
      const tile = state.board[r][c];
      if (tile) tiles.push({ tile, from: r });
      state.board[r][c] = null;
      if (isActive(state, r, c)) activeRows.push(r);
    }
    const fitting = tiles.slice(0, activeRows.length);
    const start = activeRows.length - fitting.length;
    for (let i = 0; i < fitting.length; i++) {
      const to = activeRows[start + i];
      state.board[to][c] = fitting[i].tile;
      if (fitting[i].from !== to) {
        moves.push({
          tileId: fitting[i].tile.id,
          from: { r: fitting[i].from, c },
          to: { r: to, c },
        });
      }
    }
  }
  return moves;
}

/** Fill empty playable cells. Order is column-major, top to bottom. */
export function refillEmpties(state: MatchState): RefillTile[] {
  const spawned: RefillTile[] = [];
  const h = state.config.boardH;
  const w = state.config.boardW;
  for (let c = 0; c < w; c++) {
    for (let r = 0; r < h; r++) {
      if (!isActive(state, r, c) || state.board[r][c]) continue;
      const color = TILE_COLORS[rngInt(state, TILE_COLORS.length)];
      const tile: Tile = { id: state.nextTileId++, color, special: null };
      state.board[r][c] = tile;
      spawned.push({ tileId: tile.id, position: { r, c }, color, special: null });
    }
  }
  return spawned;
}

export function swapCells(board: Cell[][], a: Position, b: Position): void {
  const tmp = board[a.r][a.c];
  board[a.r][a.c] = board[b.r][b.c];
  board[b.r][b.c] = tmp;
}

export function occupied(state: MatchState): Position[] {
  const out: Position[] = [];
  for (let r = 0; r < state.config.boardH; r++) {
    for (let c = 0; c < state.config.boardW; c++) {
      if (state.board[r][c]) out.push({ r, c });
    }
  }
  return out;
}
